// src/encounter/lib/allied-discipline.ts
//
// PROD-2947 · Andrew's walk on dev, 7 Oct (PROD-3055 b and d). An allied-health clinician's DISCIPLINE is a specialty,
// not an access level (encounter authority: "dietitian, coach, exercise physiologist, physio, SLP, psychology are
// SPECIALTIES"). Nothing in the app read it: the plan said "Your speciality is not recorded yet" for every allied
// clinician, and the allied phrase libraries (pt-, ep-, diet-, hc-, psy-) target note editors no line answered to.
//
// The discipline is read from the visit's BOOKED specialty name (the appointment record), matched by words — the
// specialty names are uncoded clinic labels. Nothing clinical is decided here: the discipline only picks which phrase
// editor a line answers to, and which existing plan framework applies (`allied-health-plan.ts`).
import type { AlliedSpeciality } from './allied-health-plan';

export type AlliedDiscipline = 'physiotherapy' | 'exercise-physiology' | 'dietetics' | 'health-coaching' | 'psychology';

/** The note editor each discipline's phrases target (the phrase libraries' `cps-phrase.editors`). */
export const DISCIPLINE_EDITOR: Readonly<Record<AlliedDiscipline, string>> = {
  physiotherapy: 'physio-note',
  'exercise-physiology': 'ep-note',
  dietetics: 'dietitian-note',
  'health-coaching': 'coaching-note',
  psychology: 'therapy-note',
};

/** Every allied discipline editor (an allied clinician whose discipline is not known is offered all of them). */
export const ALLIED_EDITORS: readonly string[] = Object.values(DISCIPLINE_EDITOR);

/** The plan framework's speciality key, for the three disciplines the plan has a framework for. */
const PLAN_SPECIALITY: Partial<Record<AlliedDiscipline, AlliedSpeciality>> = {
  dietetics: 'dietitian',
  'health-coaching': 'health-coach',
  'exercise-physiology': 'exercise-physiologist',
};

const RULES: readonly [RegExp, AlliedDiscipline][] = [
  [/\bphysio(therap(y|ist))?\b|\bphysical therap(y|ist)\b/i, 'physiotherapy'],
  [/\bexercise physiolog(y|ist)\b/i, 'exercise-physiology'],
  [/\bdiet(itian|ician|etics?)\b|\bnutrition(ist)?\b/i, 'dietetics'],
  [/\b(health )?coach(ing)?\b/i, 'health-coaching'],
  // Psychology only by name (Codex P2): "Speech Therapist" or "Occupational Therapist" is not psychology.
  [/\bpsycholog(y|ist)\b|\bpsychotherap(y|ist)\b/i, 'psychology'],
];

/** The discipline a specialty name names, or undefined. */
export function alliedDisciplineOf(specialtyName: string | null | undefined): AlliedDiscipline | undefined {
  const name = (specialtyName ?? '').trim();
  if (!name) return undefined;
  return RULES.find(([re]) => re.test(name))?.[1];
}

/**
 * The value the allied plan's `speciality` takes: its framework key when the discipline has one; otherwise the
 * specialty as recorded (the plan then says it is recorded but has no framework of its own — never "not recorded").
 */
export function planSpecialityOf(specialtyName: string | null | undefined): string | undefined {
  const name = (specialtyName ?? '').trim();
  if (!name) return undefined;
  const discipline = alliedDisciplineOf(name);
  return (discipline && PLAN_SPECIALITY[discipline]) || name;
}
